<ng-container *ngIf="form$ | async as form">
  <section class="jurisdiction-card" [formGroup]="form">
    <div class="d-flex flex-column gap-3">
      <div class="d-flex align-items-center" [style.gap.rem]="2.5">
        <div class="flex-grow-1 title-sm">
          <div class="d-flex gap-2 align-items-center">
            <span *ngIf="form.controls.governingJurisdictionId.value">
              {{ form.controls.governingJurisdictionId.value | governingJurisdictionName }}
            </span>
            <span *ngIf="!form.controls.governingJurisdictionId.value" i18n>Non-Locked-In</span>
            <app-status-pill *ngIf="isNew" [statusPill]="newStatusPill" />
          </div>
        </div>
        <div i18n class="paragraph-md secondary-text">{{ balanceLabel }} {{ estimatedBalance | currency }}</div>
      </div>
      <div class="d-flex flex-column gap-2 mins-and-maxes" [style.max-width.rem]="23.75">
        <div i18n class="paragraph-md secondary-text">Annual Min: {{ minAnnualAmount | currency }}</div>
        <div class="d-flex flex-column gap-1">
          <div i18n class="paragraph-md secondary-text">Annual Max: {{ maxAnnualAmount | currency }}</div>
          <div *ngIf="maximumRaised" class="d-flex gap-1 align-items-center">
            <img
              [src]="'assets/icons/primary/Circle Info.svg'"
              alt=""
              [style.width.rem]="0.875"
              [style.height.rem]="0.875" />
            <span class="caption secondary-text" i18n>
              Maximum raised to the minimum (spousal age minimum election).
            </span>
          </div>
        </div>
        <div class="estimated-total" i18n>
          Est. Annual Total:
          <span *ngIf="!isLoadingAnnualTotal">{{ estimatedAnnualTotal | currency }}</span>
          <span *ngIf="isLoadingAnnualTotal"><lib-loading-shimmer [width]="'5rem'" [height]="'0.75rem'" /></span>
        </div>
        <div *ngIf="startsInALaterYear" class="d-flex gap-1 align-items-start">
          <img src="assets/icons/primary/Circle Info.svg" alt="" [style.width.rem]="0.875" [style.height.rem]="0.875" />
          <span class="caption secondary-text" i18n>
            This change starts next year, so it was checked against this year's limits. Next year's are set when this
            year closes.
          </span>
        </div>
        <app-form-error
          *ngIf="formSubmitted && form.errors?.exceedsMaximum"
          [errors]="form?.errors"
          [customErrorMessages]="(customErrorMessages$ | async)?.estimatedMax ?? {}" />
      </div>
      <div *ngIf="isTargetedPayment && yearCapacity" role="status" class="d-flex gap-1 align-items-start">
        <img src="assets/icons/primary/Circle Info.svg" alt="" [style.width.rem]="0.875" [style.height.rem]="0.875" />
        <span class="caption secondary-text" i18n>
          These accounts hold {{ yearCapacity.availableThisYear | currency }} available this year, and this payment asks
          for {{ yearCapacity.askedThisYear | currency }} over {{ yearCapacity.paymentsLeft }}. About
          {{ yearCapacity.paymentsInFull }}
          {yearCapacity.paymentsInFull, plural, =1 {payment} other {payments}} can be made in full before the money
          available this year runs out, after which payments reduce.
        </span>
      </div>
    </div>
    <div class="pt-2 d-flex align-items-center justify-content-between gap-3">
      <div class="d-flex flex-column" [style.gap.rem]="0.75">
        <div *ngIf="!isBoardDirected && !isTargetedPayment" class="d-flex align-items-center" [style.gap.rem]="0.75">
          <app-radio-button-input
            *ngIf="form.controls.recurringPaymentType.enabled"
            [controlId]="form.controls.governingJurisdictionId.value + '-custom'"
            [name]="form.controls.governingJurisdictionId.value + 'payment-type'"
            [value]="RecurringPaymentAmountTypeEnum.Custom"
            formControlName="recurringPaymentType" />
          <app-form-option-label [labelFor]="form.controls.governingJurisdictionId.value + '-custom'" i18n
            >Custom Payment</app-form-option-label
          >
        </div>
        <div class="d-flex align-items-center" [style.gap.rem]="0.75">
          <app-radio-button-input
            *ngIf="isBoardDirected || form.controls.recurringPaymentType.enabled"
            [controlId]="form.controls.governingJurisdictionId.value + '-min'"
            [name]="form.controls.governingJurisdictionId.value + 'payment-type'"
            [value]="RecurringPaymentAmountTypeEnum.Minimum"
            formControlName="recurringPaymentType" />
          <div class="d-flex align-items-center gap-1">
            <app-form-option-label i18n [labelFor]="form.controls.governingJurisdictionId.value + '-min'"
              >Minimum Payment: {{ minimumPaymentAmount | currency }}</app-form-option-label
            >
            <button
              type="button"
              class="no-style-button"
              aria-label="Minimum payment information"
              i18n-aria-label
              [ngbTooltip]="minTooltip$ | async"
              [triggers]="'hover focus click'">
              <img
                src="assets/icons/grey/CircleQuestion.svg"
                alt=""
                [style.height.rem]="1.125"
                [style.width.rem]="1.125" />
            </button>
          </div>
        </div>
        <div
          *ngIf="
            !isBoardDirected &&
            !(
              (paymentFrequency$ | async) === VariableBenefitPaymentFrequencyEnum.Annual &&
              !form.controls.governingJurisdictionId.value
            )
          "
          class="d-flex align-items-center"
          [style.gap.rem]="0.75">
          <div *ngIf="form.controls.recurringPaymentType.enabled" [style.min-width.rem]="1.5">
            <app-radio-button-input
              *ngIf="form.controls.recurringPaymentType.enabled && form.controls.governingJurisdictionId.value"
              [controlId]="form.controls.governingJurisdictionId.value + '-max'"
              [name]="form.controls.governingJurisdictionId.value + 'payment-type'"
              [value]="RecurringPaymentAmountTypeEnum.Maximum"
              formControlName="recurringPaymentType" />
          </div>
          <div class="d-flex align-items-center gap-1">
            <app-form-option-label i18n [labelFor]="form.controls.governingJurisdictionId.value + '-max'"
              >Maximum Payment: {{ maximumPaymentAmount | currency }}</app-form-option-label
            >
            <button
              type="button"
              class="no-style-button"
              aria-label="Maximum payment information"
              i18n-aria-label
              [ngbTooltip]="maxTooltip$ | async"
              [triggers]="'hover focus click'">
              <img
                src="assets/icons/grey/CircleQuestion.svg"
                alt=""
                [style.height.rem]="1.125"
                [style.width.rem]="1.125" />
            </button>
          </div>
        </div>
      </div>
      <div class="d-flex flex-column gap-1" [style.width.rem]="18.75">
        <label i18n class="form-input-label" [for]="form.controls.governingJurisdictionId.value + 'payment-amount'"
          >Payment Amount</label
        >
        <app-recurring-currency-input
          [hidden]="calculatingTargetedPayment"
          [controlId]="form.controls.governingJurisdictionId.value + 'payment-amount'"
          [error]="
            formSubmitted &&
            ((!form.controls.hasLumpSumPayment.value && form?.errors?.exceedsMaximum) ||
              form?.controls?.recurringPaymentAmount.invalid)
          "
          formControlName="recurringPaymentAmount"
          [frequency]="
            (paymentFrequency$ | async) === VariableBenefitPaymentFrequencyEnum.Annual ? 'annual' : 'monthly'
          " />
        <app-text-input [formControl]="loadingControl" [hidden]="!calculatingTargetedPayment" />
        <app-form-error
          *ngIf="formSubmitted && form?.controls?.recurringPaymentAmount?.invalid"
          [errors]="form?.controls?.recurringPaymentAmount?.errors"
          [customErrorMessages]="(customErrorMessages$ | async)?.recurringPaymentAmount" />
      </div>
    </div>
  </section>
</ng-container>
